import { call, put, select } from 'redux-saga/effects'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import {
  initAdditionalSales,
  updateAdditionalSalesConfig,
  updateAdditionalSalesData,
} from '../../SubscriptionForm/SubscriptionFormSlice'
import {
  selectAdditionalSaleDataByName,
  selectAdditionalSalesConfig,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { groupAndSortByGroupAndOrder } from './additionalSalesHelper'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { callApi as callApiV2 } from '@/shared/tools/apiSagaHelperV2'
import * as Sentry from '@sentry/react'

export const additionalSalesActions = {
  V2_ADDITIONAL_SALES_SEND_VALIDATION_CODE: 'V2_ADDITIONAL_SALES_SEND_VALIDATION_CODE',
  V2_ADDITIONAL_SALES_OPEN_SIGN_LINK: 'V2_ADDITIONAL_SALES_OPEN_SIGN_LINK',
  PROTECTED_SEND_SMS: 'PROTECTED_SEND_SMS',
  PROTECTED_SEND_NEW_OTP: 'PROTECTED_SEND_NEW_OTP',
  LOAD_ADDITIONAL_SALES_CONFIG: 'LOAD_ADDITIONAL_SALES_CONFIG',
  LOAD_EXISTING_ADDITIONAL_SALES: 'LOAD_EXISTING_ADDITIONAL_SALES',
  GENERATE_GARANKA_URL: 'GENERATE_GARANKA_URL',
  AXA_SEND_MAIL: 'AXA_SEND_MAIL',
  VOLTALIS_APPOINTMENT_URL: 'VOLTALIS_APPOINTMENT_URL',
  VOLTALIS_STATUS_URL: 'VOLTALIS_STATUS_URL',
  VOLTALIS_AVAILABILITIES_URL: 'VOLTALIS_AVAILABILITIES_URL',
  VOLTALIS_CUSTOMER_CALL_URL: 'VOLTALIS_CUSTOMER_CALL_URL',
  NEAT_RESEND_EMAIL_URL: 'NEAT_RESEND_EMAIL_URL',
  CREATE_ADDITIONAL_SALE: 'CREATE_ADDITIONAL_SALE',
  RETRY_ADDITIONAL_SALE: 'RETRY_ADDITIONAL_SALE',
  SEND_OPT_IN_SMS: 'SEND_OPT_IN_SMS',
}

export function* createAdditionalSale({ url, body, callback }) {
  try {
    const response = yield call(callApi, {
      endpoint: url,
      showErrorSnackbar: false,
      method: 'POST',
      payload: body,
    })
    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('createAdditionalSaleError'), { extra: { error } })
    }
    yield call(callback, error)
  }
}

export function* retryAdditionalSale({ url, callback }) {
  try {
    const response = yield call(callApi, {
      endpoint: url,
      showErrorSnackbar: false,
      method: 'POST',
    })
    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422 && status !== 409) {
      Sentry.captureException(new Error('retryAdditionalSaleError'), { extra: { error } })
    }
    yield call(callback, error)
  }
}

export function* sendOptInSmsForAdditionalSale({ contactId, saleType, callback }) {
  const { additionalSaleOptInSmsUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const response = yield call(callApi, {
      endpoint: additionalSaleOptInSmsUrl,
      asteriskReplacement: contactId,
      showErrorSnackbar: false,
      payload: { sale_type: saleType },
      method: 'POST',
    })
    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('additionalSaleOptInSmsError'), { extra: { error } })
    }
    yield call(callback, error)
  }
}

export function* sendAxaMail({ options, callback, callbackError }) {
  const { axaSendMailUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const response = yield call(callApi, {
      endpoint: axaSendMailUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })

    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('sendAxaMailError'), { extra: { error } })
    }
    yield call(callbackError)
  }
}

export function* generateGarankaUrl({ options, callback, callbackError }) {
  const { garankaEncryptUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const response = yield call(callApi, {
      endpoint: garankaEncryptUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })

    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('generateGarankaUrlError'), { extra: { error } })
    }
    yield call(callbackError)
  }
}

export function* selectVoltalisAppointment({ options }) {
  const { voltalisAppointmentUrl } = yield select(selectExtraRoutesAuthData)

  try {
    yield call(callApiV2, {
      endpoint: voltalisAppointmentUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
  } catch (error) {
    // Silently fail
  }
}

export function* getVoltalisStatus({ options }) {
  const { voltalisStatusUrl } = yield select(selectExtraRoutesAuthData)

  try {
    yield call(callApiV2, {
      endpoint: voltalisStatusUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
  } catch (error) {
    // Silently fail
  }
}

export function* getVoltalisAvailabilities({ options }) {
  const { voltalisAvailabilitiesUrl } = yield select(selectExtraRoutesAuthData)

  try {
    yield call(callApiV2, {
      endpoint: voltalisAvailabilitiesUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
  } catch (error) {
    // Silently fail
  }
}

export function* voltalisCustomerCall({ options }) {
  const { voltalisCustomerCallUrl } = yield select(selectExtraRoutesAuthData)

  try {
    yield call(callApiV2, {
      endpoint: voltalisCustomerCallUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
  } catch (error) {
    // Silently fail
  }
}

export function* neatResendEmail({ options, callback, callbackError }) {
  const { neatResendEmailUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const response = yield call(callApi, {
      endpoint: neatResendEmailUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: options,
    })
    yield call(callback, response)
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('neatResendEmailError'), { extra: { error } })
    }
    yield call(callbackError)
  }
}

export function* loadExistingAdditionalSales({ contactId }) {
  const { existingSalesLoaded } = yield select(selectAdditionalSalesConfig)
  if (existingSalesLoaded) {
    return
  }

  const { existingAdditionalSalesUrl } = yield select(selectExtraRoutesAuthData)
  try {
    const result = yield call(callApi, {
      endpoint: existingAdditionalSalesUrl?.replace('*', contactId),
      showErrorSnackbar: false,
      method: 'GET',
    })

    const { retriedSales, blockedSales, blockedSalesReasons, ...additionalSalesData } = result || {}

    yield put(
      updateAdditionalSalesConfig({
        existingSalesLoaded: true,
        retriedSales: retriedSales || [],
        blockedSales: blockedSales || [],
        blockedSalesReasons: blockedSalesReasons || {},
      }),
    )
    yield put(initAdditionalSales(additionalSalesData))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('loadExistingAdditionalSalesError'), { extra: { error } })
    }
  }
}

export function* loadAdditionalSaleConfig() {
  const { salesConfigLoaded } = yield select(selectAdditionalSalesConfig)
  if (salesConfigLoaded) {
    return
  }

  const { additionalSalesProductsListUrl } = yield select(selectExtraRoutesAuthData)
  try {
    const result = yield call(callApi, {
      endpoint: additionalSalesProductsListUrl,
      showErrorSnackbar: false,
      method: 'GET',
    })
    const config = yield call(groupAndSortByGroupAndOrder, result ?? {})
    yield put(updateAdditionalSalesConfig({ salesConfigLoaded: true, sales: result, groupConfig: config }))
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('loadAdditionalSalesConfigError'), { extra: { error } })
    }
  }
}

export function* openPandaDocSignLink({ zohoId }) {
  const { pandadocSignLinkUrl } = yield select(selectExtraRoutesAuthData)
  yield put(updateAdditionalSalesConfig({ openingSignLink: true }))
  try {
    const result = yield call(callApi, {
      endpoint: pandadocSignLinkUrl + '/' + zohoId + '?version=v2',
      showErrorSnackbar: false,
      method: 'GET',
    })
    yield put(updateAdditionalSalesConfig({ openingSignLink: false }))
    if (result && result.url) {
      yield call([window, 'open'], result.url, '_blank')
    }
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('openPandaDocSignLinkError'), { extra: { error } })
    }
    yield put(updateAdditionalSalesConfig({ openingSignLink: false }))
  }
}

export function* sendAdditionalSalesCodeValidation({ leadType, zohoId, email, phone, contactId }) {
  const { pandadocCodeDeliveryUrl } = yield select(selectExtraRoutesAuthData)
  yield put(
    updateAdditionalSalesConfig({
      sendingCode: true,
      sendCodeError: null,
    }),
  )

  try {
    const result = yield call(callApi, {
      endpoint: pandadocCodeDeliveryUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload: { leadType, subscriptionId: contactId, zohoId, email, phone, version: 'v2' },
    })

    if (result && !result.errorCode) {
      const sale = yield select(selectAdditionalSaleDataByName(leadType))
      yield put(
        updateAdditionalSalesData({
          name: leadType,
          data: { code_status: null, infoAttributes: { ...sale?.infoAttributes, code_status: null } },
        }),
      )
      yield put(updateAdditionalSalesConfig({ sendingCode: false }))
    } else {
      yield put(
        updateAdditionalSalesConfig({
          sendingCode: false,
          sendCodeError: result.message || JSON.stringify(result),
        }),
      )
    }
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('sendAdditionalSalesCodeValidationError'), { extra: { error } })
    }
    yield put(
      updateAdditionalSalesConfig({
        sendingCode: false,
        sendCodeError: error.message || JSON.stringify(error),
      }),
    )
  }
}

export function* protectedSendSms({ payload }) {
  const { protectedSignUrl } = yield select(selectExtraRoutesAuthData)
  try {
    yield call(callApi, {
      endpoint: protectedSignUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload,
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('protectedSignError'), { extra: { error } })
    }
  }
}

export function* protectedSendNewOtp({ payload }) {
  const { protectedSendOtpUrl } = yield select(selectExtraRoutesAuthData)
  try {
    yield call(callApi, {
      endpoint: protectedSendOtpUrl,
      showErrorSnackbar: false,
      method: 'POST',
      payload,
    })
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error('protectedSendNewOtpError'), { extra: { error } })
    }
  }
}
